import { useI18n } from '@/lib/i18n';
import type { Engine, QwenRegion } from './cost-model';
import { engineName } from './ledger-labels';
import { qwenProviderFor, sonioxProvider, type PricingResponse } from './pricing-client';

interface RateRow {
  labelKey: string;
  usdPerMillion: number;
  useKey: 'usePerConnectedHour' | 'usePerSpeechHour' | 'usePerSpokenHour' | 'usePerScreenHour';
  tokensPerHour: number;
  /** CNY per million tokens, for the rows of an entry that publishes it (Beijing's). */
  cnyPerMillion?: number;
}

/**
 * The full rate card for ONE engine, folded under the cost calculator.
 *
 * NEVER BOTH ENGINES. The page does not put the two engines' prices side by
 * side anywhere; this fold follows the calculator's engine switch, so it lists
 * the unit prices behind the figure the visitor is looking at and nothing else.
 *
 * DOLLARS READ FIRST, TOKENS SETTLE. Each engine bills per token, the only
 * exact figures here are the per-1M-token prices, and every dollar figure on
 * the page is a conversion of them — which is why the settlement note sits
 * directly under the table. The tokens-per-hour column is the conversion the
 * calculator prices with, written out.
 *
 * Nothing is hardcoded: every figure comes from `/api/pricing`, fetched once
 * for the page by `usePricing`.
 */
export function PricingCard({ pricing, engine, region }: {
  pricing: PricingResponse | null;
  engine: Engine;
  region: QwenRegion;
}) {
  const { t, locale } = useI18n();
  // Fixed 2dp rather than `@/lib/money-format`: a column of unit prices wants
  // one width, and that helper varies precision by magnitude.
  const money = (usd: number) => `$${usd.toFixed(2)}`;
  // Locale-aware: without the argument this follows the BROWSER's locale while
  // every other number on the page follows the chosen language.
  const count = (n: number) => Math.round(n).toLocaleString(locale);
  const fill = (key: string, token: string, value: string) => t(key).replace(token, value);

  const soniox = engine === 'soniox' ? sonioxProvider(pricing) : null;
  const qwen = engine === 'qwen' ? qwenProviderFor(pricing, region) : null;
  if (!pricing) return null;

  let rows: RateRow[];
  let connectedHourTokens: number;
  if (soniox) {
    const r = soniox.tokenRatesUsdPerMillion;
    const c = soniox.tokenConversions;
    rows = [
      { labelKey: 'rateInputAudio', usdPerMillion: r.sttInputAudio, useKey: 'usePerConnectedHour', tokensPerHour: c.sttInputAudioPerConnectedHour },
      { labelKey: 'rateOutputText', usdPerMillion: r.sttOutputText, useKey: 'usePerSpeechHour', tokensPerHour: c.sttOutputTextPerSpeechHour },
      { labelKey: 'rateTtsInputText', usdPerMillion: r.ttsInputText, useKey: 'usePerSpokenHour', tokensPerHour: c.ttsInputTextPerGeneratedHour },
      { labelKey: 'rateTtsOutputAudio', usdPerMillion: r.ttsOutputAudio, useKey: 'usePerSpokenHour', tokensPerHour: c.ttsOutputAudioPerGeneratedHour },
    ];
    connectedHourTokens = c.sttInputAudioPerConnectedHour;
  } else if (qwen) {
    const r = qwen.tokenRatesUsdPerMillion;
    const cny = qwen.tokenRatesCnyPerMillion;
    const c = qwen.tokenConversions;
    rows = [
      { labelKey: 'rateInputAudio', usdPerMillion: r.audioIn, cnyPerMillion: cny?.audioIn, useKey: 'usePerConnectedHour', tokensPerHour: c.audioInTokensPerSecond * 3600 },
      { labelKey: 'rateOutputText', usdPerMillion: r.textOut, cnyPerMillion: cny?.textOut, useKey: 'usePerSpeechHour', tokensPerHour: c.textOutTokensPerSecondOfSpeech * 3600 },
      { labelKey: 'rateTtsOutputAudio', usdPerMillion: r.audioOut, cnyPerMillion: cny?.audioOut, useKey: 'usePerSpokenHour', tokensPerHour: c.audioOutTokensPerSecondOfSpeech * 3600 },
    ];
    // Screen context's image tokens, from a payload that publishes them: a cached body from
    // before the add-on has neither the rate nor the conversion, and the row is left out.
    if (r.imageIn !== undefined && c.imageInTokensPerSecondOfSpeech !== undefined) {
      rows.push({ labelKey: 'rateInputImage', usdPerMillion: r.imageIn, cnyPerMillion: cny?.imageIn, useKey: 'usePerScreenHour', tokensPerHour: c.imageInTokensPerSecondOfSpeech * 3600 });
    }
    connectedHourTokens = c.audioInTokensPerSecond * 3600;
  } else {
    // The selected engine's entry is not in the payload (a cached body from
    // before it was published): the calculator shows "unavailable" above.
    return null;
  }

  // Beijing's entry alone carries its CNY rates and the rate behind its dollars.
  const showCny = rows.some((row) => row.cnyPerMillion !== undefined);
  const fx = qwen?.fx;

  return (
    <div className="wallet-card__content">
      <p className="wallet-card__pricing-provider">
        {soniox
          ? t('dashboard.wallet.pricing.provider')
          : fill('dashboard.wallet.pricing.providerQwen', '{name}', engineName('qwen', region, t) ?? 'Qwen')}
      </p>

      <div className="wallet-card__pricing-scroll">
        <table className="wallet-card__pricing-table">
          <thead>
            <tr>
              <th>{t('dashboard.wallet.pricing.colItem')}</th>
              <th colSpan={showCny ? 2 : undefined}>{t('dashboard.wallet.pricing.colUnitPrice')}</th>
              <th>{t('dashboard.wallet.pricing.colHourly')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.labelKey}>
                <td>{t(`dashboard.wallet.pricing.${row.labelKey}`)}</td>
                <td>{fill('dashboard.wallet.pricing.perMillion', '{amount}', money(row.usdPerMillion))}</td>
                {/* The template carries the currency sign, so the amount is bare,
                    in the same fixed 2dp as the dollar cell beside it. */}
                {showCny && (
                  <td>
                    {row.cnyPerMillion !== undefined &&
                      fill('dashboard.wallet.pricing.cnyPerMillion', '{amount}', row.cnyPerMillion.toFixed(2))}
                  </td>
                )}
                <td>{fill(`dashboard.wallet.pricing.${row.useKey}`, '{count}', count(row.tokensPerHour))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="wallet-card__pricing-note">{t('dashboard.wallet.pricing.assumptions')}</p>
      <p className="wallet-card__pricing-note">{t('dashboard.wallet.pricing.settlement')}</p>
      {/* The rate the dollar column was converted at, to the four decimals the
          admin page shows it to, with the day it was taken. */}
      {fx && (
        <p className="wallet-card__pricing-note">
          {fill('dashboard.wallet.pricing.fxNote', '{date}', fx.date).replace('{rate}', fx.cnyPerUsd.toFixed(4))}
        </p>
      )}

      {/* Soniox's own conversions, quoted from the vendor, and the date its
          prices were read: they describe Soniox, so they stay under Soniox. */}
      {soniox && (
        <>
          <p className="wallet-card__pricing-reference">{t('dashboard.wallet.pricing.sttReference')}</p>
          <p className="wallet-card__pricing-reference">{t('dashboard.wallet.pricing.ttsReference')}</p>
          <p className="wallet-card__pricing-note">{t('dashboard.wallet.pricing.referenceNote')}</p>
          <p className="wallet-card__pricing-note">
            {fill('dashboard.wallet.pricing.readOn', '{date}', pricing.pricesReadOn)}
          </p>
        </>
      )}

      <h3 className="wallet-card__pricing-subheading">{t('dashboard.wallet.pricing.boundariesHeading')}</h3>
      <ul className="wallet-card__pricing-boundaries">
        <li>{fill('dashboard.wallet.pricing.boundaryConnection', '{count}', count(connectedHourTokens))}</li>
        <li>{t('dashboard.wallet.pricing.boundaryReservation')}</li>
        {/* The validity term is NOT restated here. This is the Terms of
            Service sentence, already translated in all twelve catalogs; a
            second phrasing would give a disputing user a discrepancy between
            this page and the ToS to point at. */}
        <li>{t('tos.tokens.purchase.items').split('|').slice(-1)[0].trim()}</li>
        {!showCny && <li>{t('dashboard.wallet.pricing.boundaryCurrency')}</li>}
      </ul>

      <h3 className="wallet-card__pricing-subheading">{t('dashboard.wallet.pricing.faqHeading')}</h3>
      <dl className="wallet-card__pricing-faq">
        <dt>{t('dashboard.wallet.pricing.faqTranslationQ')}</dt>
        <dd>{t('dashboard.wallet.pricing.faqTranslationA')}</dd>
        {/* Diarization, language identification and formatting are Soniox's
            features; the question has no Qwen answer. */}
        {soniox && (
          <>
            <dt>{t('dashboard.wallet.pricing.faqFeaturesQ')}</dt>
            <dd>{t('dashboard.wallet.pricing.faqFeaturesA')}</dd>
          </>
        )}
      </dl>
    </div>
  );
}
